import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ExternalLink, Plus, RefreshCw, Save, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const fieldValue = (field) => field?.field_id || field?.api_name || field?.label || '';

const providerEntities = [
  { key: 'contact', label: 'Customer' },
  { key: 'invoice', label: 'Invoice' },
];

const emptyMappingRow = () => ({
  id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  seatifyKey: '',
  providerValue: '',
});

const statusLabel = (connection) => {
  if (!connection) return 'Ready to connect';
  if (connection.status === 'select_organization') return 'Choose organization';
  if (connection.status === 'connected' && connection.organizationId) return 'Connected';
  return connection.status || 'Pending';
};

const statusClass = (connection) => {
  if (connection?.status === 'connected' && connection?.organizationId) return 'text-emerald-600 bg-emerald-50 border-emerald-100';
  if (connection?.status === 'select_organization') return 'text-amber-700 bg-amber-50 border-amber-100';
  return 'text-slate-600 bg-slate-50 border-slate-200';
};

function SearchableSelect({
  value,
  options = [],
  onChange,
  placeholder,
  emptyMessage = 'No options found',
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selectedOption = options.find(option => String(option.value) === String(value));

  useEffect(() => {
    if (!open) setQuery(selectedOption?.label || '');
  }, [open, selectedOption?.label]);

  const filteredOptions = options.filter((option) => {
    const needle = query.trim().toLowerCase();
    if (!needle || query === selectedOption?.label) return true;
    return `${option.label} ${option.searchText || ''}`.toLowerCase().includes(needle);
  });

  const selectOption = (option) => {
    onChange(option.value);
    setQuery(option.label);
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={open ? query : selectedOption?.label || ''}
        onFocus={(event) => {
          setOpen(true);
          setQuery(selectedOption?.label || '');
          event.target.select();
        }}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 120);
        }}
        onChange={(event) => {
          const nextQuery = event.target.value;
          setQuery(nextQuery);
          setOpen(true);
          if (!nextQuery) onChange('');
        }}
        disabled={disabled}
        placeholder={placeholder}
        autoComplete="off"
        className="w-full px-3 py-2 pr-9 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-400"
      />
      <ChevronDown
        size={16}
        className={`pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
      />
      {open && !disabled && (
        <div className="absolute z-50 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm font-semibold text-slate-400">{emptyMessage}</div>
          ) : filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                selectOption(option);
              }}
              className={`block w-full px-3 py-2 text-left text-sm font-semibold hover:bg-indigo-50 hover:text-indigo-700 ${
                String(option.value) === String(value) ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ErpIntegrationsPage({ scope = 'institution' }) {
  const { user } = useAuth();
  const { collegeName } = useParams();
  const navigate = useNavigate();
  const [institution, setInstitution] = useState(null);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingSlug, setConnectingSlug] = useState('');
  const [selectedOrganizations, setSelectedOrganizations] = useState({});
  const [savingOrganization, setSavingOrganization] = useState('');
  const [savingMappings, setSavingMappings] = useState('');
  const [refreshingFields, setRefreshingFields] = useState('');
  const [mappings, setMappings] = useState({});
  const [openSections, setOpenSections] = useState({});

  const isInstitutionScope = scope === 'institution';
  const apiBase = isInstitutionScope ? '/api/institution-admin' : '/api/admin';
  const routeBase = isInstitutionScope ? '/institution' : scope === 'cluster' ? '/cluster-admin' : '/admin';

  const institutionIdParams = useMemo(() => (
    !isInstitutionScope && institution?._id ? { institutionId: institution._id } : {}
  ), [isInstitutionScope, institution?._id]);

  useEffect(() => {
    fetchInstitution();
  }, [collegeName, scope]);

  useEffect(() => {
    if (isInstitutionScope || institution?._id) fetchPlatforms();
  }, [isInstitutionScope, institution?._id]);

  const fetchInstitution = async () => {
    setLoading(true);
    try {
      if (isInstitutionScope) {
        const res = await axios.get('/api/institution-admin/me');
        setInstitution(res.data?.institution || null);
      } else {
        const decodedName = decodeURIComponent(collegeName || '');
        const res = await axios.get(`${apiBase}/institutions`);
        const items = res.data?.items || (Array.isArray(res.data) ? res.data : []);
        setInstitution(items.find(item => item.collegeName === decodedName || item.displayName === decodedName) || null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load institution');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatforms = async () => {
    try {
      const res = await axios.get('/api/erp/platforms', { params: institutionIdParams });
      setPlatforms(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load ERP platforms');
    }
  };

  const updatePlatformConnection = (connectionId, nextConnection) => {
    setPlatforms(prev => prev.map(platform => (
      platform.connection?.id === connectionId ? { ...platform, connection: nextConnection } : platform
    )));
  };

  const handleConnect = async (platform) => {
    setConnectingSlug(platform.slug);
    try {
      const res = await axios.post(`/api/erp/platforms/${platform.slug}/connect`, {
        ...institutionIdParams,
        returnPath: `${window.location.pathname}${window.location.search}`,
      });
      if (res.data?.authorizationUrl) window.location.href = res.data.authorizationUrl;
      else toast.error('Authorization URL was not returned');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start ERP connection');
      setConnectingSlug('');
    }
  };

  const handleSelectOrganization = async (platform) => {
    const connection = platform.connection;
    const organizationId = selectedOrganizations[connection?.id] || connection?.organizationId || connection?.organizations?.[0]?.organization_id || '';
    if (!connection?.id || !organizationId) {
      toast.error('Select a Zoho organization');
      return;
    }
    setSavingOrganization(connection.id);
    try {
      const res = await axios.post(`/api/erp/connections/${connection.id}/organization`, {
        organizationId,
        ...institutionIdParams,
      });
      updatePlatformConnection(connection.id, res.data?.connection);
      toast.success('Zoho organization connected');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save Zoho organization');
    } finally {
      setSavingOrganization('');
    }
  };

  const mappingRowsFromConnection = (connection, entity) => {
    const saved = connection?.fieldMappings?.[entity] || {};
    const rows = Object.entries(saved)
      .map(([seatifyKey, field]) => ({
        id: `${entity}-${seatifyKey}`,
        seatifyKey,
        providerValue: fieldValue(field),
      }))
      .filter(row => row.seatifyKey && row.providerValue);
    return rows.length ? rows : [emptyMappingRow()];
  };

  const getMappingRows = (connection, entity) => (
    mappings[connection?.id]?.[entity] || mappingRowsFromConnection(connection, entity)
  );

  const setMappingRows = (connectionId, entity, rows) => {
    setMappings(prev => ({
      ...prev,
      [connectionId]: {
        ...(prev[connectionId] || {}),
        [entity]: rows,
      },
    }));
  };

  const updateMappingRow = (connection, entity, rowId, patch) => {
    const rows = getMappingRows(connection, entity).map(row => (
      row.id === rowId ? { ...row, ...patch } : row
    ));
    setMappingRows(connection.id, entity, rows);
  };

  const addMappingRow = (connection, entity) => {
    setMappingRows(connection.id, entity, [...getMappingRows(connection, entity), emptyMappingRow()]);
  };

  const removeMappingRow = (connection, entity, rowId) => {
    const rows = getMappingRows(connection, entity).filter(row => row.id !== rowId);
    setMappingRows(connection.id, entity, rows.length ? rows : [emptyMappingRow()]);
  };

  const toggleSection = (connectionId, entity) => {
    const key = `${connectionId}-${entity}`;
    setOpenSections(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const isSectionOpen = (connectionId, entity) => openSections[`${connectionId}-${entity}`] !== false;

  const buildMappingsPayload = (connection) => {
    const payload = {};
    providerEntities.forEach(({ key: entity }) => {
      payload[entity] = {};
      getMappingRows(connection, entity).forEach((row) => {
        if (row.seatifyKey && row.providerValue) payload[entity][row.seatifyKey] = row.providerValue;
      });
    });
    return payload;
  };

  const handleSaveMappings = async (platform) => {
    const connection = platform.connection;
    if (!connection?.id) return;
    setSavingMappings(connection.id);
    try {
      const res = await axios.post(`/api/erp/connections/${connection.id}/field-mappings`, {
        mappings: buildMappingsPayload(connection),
        ...institutionIdParams,
      });
      updatePlatformConnection(connection.id, res.data?.connection);
      toast.success('ERP field mapping saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save field mapping');
    } finally {
      setSavingMappings('');
    }
  };

  const handleRefreshFields = async (platform) => {
    const connection = platform.connection;
    if (!connection?.id) return;
    setRefreshingFields(connection.id);
    try {
      const res = await axios.post(`/api/erp/connections/${connection.id}/fields/refresh`, institutionIdParams);
      updatePlatformConnection(connection.id, res.data?.connection);
      toast.success('ERP columns refreshed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh ERP columns');
    } finally {
      setRefreshingFields('');
    }
  };

  const backPath = isInstitutionScope
    ? '/institution/courses'
    : `${routeBase}/colleges/${encodeURIComponent(collegeName || '')}`;

  if (loading) {
    return <div className="h-64 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <button type="button" onClick={() => navigate(backPath)} className="inline-flex items-center gap-2 text-indigo-600 font-bold hover:text-indigo-700">
            <ArrowLeft size={18} /> Back
          </button>
          <h1 className="mt-4 text-3xl font-extrabold text-slate-900">Connect your ERP Platform</h1>
          <p className="mt-1 text-sm font-semibold text-slate-500">{institution?.displayName || institution?.collegeName || 'Institution'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-5">
        <div className="space-y-3">
          {platforms.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-5 text-sm font-semibold text-slate-500">No ERP platforms are available.</div>
          ) : platforms.map((platform) => {
            const connection = platform.connection;
            const organizations = connection?.organizations || [];
            const selectedOrg = selectedOrganizations[connection?.id] || connection?.organizationId || organizations[0]?.organization_id || '';
            return (
              <div key={platform.id || platform._id || platform.slug} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg border border-slate-100 flex items-center justify-center overflow-hidden">
                    <img src={platform.logoUrl} alt={platform.name} className="h-full w-full object-contain p-2" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-extrabold text-slate-900 truncate">{platform.name}</p>
                    <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${statusClass(connection)}`}>
                      {statusLabel(connection)}
                    </span>
                  </div>
                  <ExternalLink size={16} className="text-slate-400" />
                </div>

                {connection?.status === 'select_organization' && (
                  <div className="mt-4 space-y-3">
                    <SearchableSelect
                      value={selectedOrg}
                      onChange={(value) => setSelectedOrganizations(prev => ({ ...prev, [connection.id]: value }))}
                      placeholder="Search Zoho organization"
                      emptyMessage="No Zoho organizations found"
                      options={organizations.map((org) => {
                        const label = `${org.name || org.organization_id}${org.is_default_org ? ' (Default)' : ''}`;
                        return {
                          value: org.organization_id,
                          label,
                          searchText: `${label} ${org.organization_id}`,
                        };
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => handleSelectOrganization(platform)}
                      disabled={savingOrganization === connection.id}
                      className="w-full px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-70"
                    >
                      {savingOrganization === connection.id ? 'Saving...' : 'Connect Organization'}
                    </button>
                  </div>
                )}

                {connection?.status !== 'select_organization' && (
                  <button
                    type="button"
                    onClick={() => handleConnect(platform)}
                    disabled={connectingSlug === platform.slug}
                    className="mt-4 w-full px-4 py-2 rounded-lg border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
                  >
                    {connectingSlug === platform.slug ? 'Redirecting...' : connection?.status === 'connected' ? 'Reconnect' : `Connect ${platform.name}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <div className="space-y-5">
          {platforms.filter((platform) => platform.connection?.status === 'connected').map((platform) => {
            const connection = platform.connection;
            const seatifyFields = connection?.seatifyFields || {};
            return (
              <div key={`${platform.slug}-mapping`} className="rounded-lg border border-slate-200 bg-white">
                <div className="border-b border-slate-100 px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-lg font-extrabold text-slate-900">{platform.name} Field Mapping</h2>
                    <p className="text-sm font-semibold text-slate-500">Add only the mappings you want to sync.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRefreshFields(platform)}
                    disabled={refreshingFields === connection.id}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-70"
                  >
                    <RefreshCw size={16} /> {refreshingFields === connection.id ? 'Refreshing...' : 'Refresh Columns'}
                  </button>
                </div>

                <div className="p-5 space-y-6">
                  {providerEntities.map(({ key: entity, label }) => {
                    const providerFields = connection?.customFields?.[entity] || [];
                    const rows = getMappingRows(connection, entity);
                    const sectionOpen = isSectionOpen(connection.id, entity);
                    const selectedSeatifyKeys = rows.map(row => row.seatifyKey).filter(Boolean);
                    const selectedProviderValues = rows.map(row => row.providerValue).filter(Boolean);
                    return (
                      <section key={entity} className="relative rounded-lg border border-slate-200">
                        <button
                          type="button"
                          onClick={() => toggleSection(connection.id, entity)}
                          className="w-full px-4 py-3 bg-slate-50 flex items-center justify-between gap-3 text-left"
                        >
                          <span>
                            <span className="block text-sm font-extrabold uppercase text-slate-600">{label}</span>
                            <span className="block text-xs font-bold text-slate-400">{providerFields.length} Zoho columns found · {rows.filter(row => row.seatifyKey && row.providerValue).length} mapped</span>
                          </span>
                          <ChevronDown size={18} className={`text-slate-500 transition-transform ${sectionOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {sectionOpen && (
                          <div className="p-4 space-y-3 bg-white border-t border-slate-200">
                            {providerFields.length === 0 && (
                              <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700">
                                No {label.toLowerCase()} columns were returned by this ERP. Create the columns in the ERP, then refresh columns here.
                              </div>
                            )}

                            <div className="hidden md:grid md:grid-cols-[1fr_1fr_44px] gap-3 px-1 text-xs font-extrabold uppercase text-slate-400">
                              <div>Seatify column</div>
                              <div>Zoho column</div>
                              <div></div>
                            </div>

                            {rows.map((row) => (
                              <div key={row.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_44px] gap-3">
                                <SearchableSelect
                                  value={row.seatifyKey}
                                  onChange={(value) => updateMappingRow(connection, entity, row.id, { seatifyKey: value })}
                                  placeholder="Search Seatify column"
                                  emptyMessage="No Seatify columns found"
                                  options={Object.entries(seatifyFields)
                                    .filter(([seatifyKey]) => row.seatifyKey === seatifyKey || !selectedSeatifyKeys.includes(seatifyKey))
                                    .map(([seatifyKey, seatifyLabel]) => ({
                                      value: seatifyKey,
                                      label: `${seatifyLabel} (${seatifyKey})`,
                                      searchText: `${seatifyLabel} ${seatifyKey}`,
                                    }))}
                                />

                                <SearchableSelect
                                  value={row.providerValue}
                                  onChange={(value) => updateMappingRow(connection, entity, row.id, { providerValue: value })}
                                  disabled={providerFields.length === 0}
                                  placeholder={providerFields.length === 0 ? 'No Zoho columns available' : 'Search Zoho column'}
                                  emptyMessage="No Zoho columns found"
                                  options={providerFields
                                    .filter((field) => {
                                      const value = fieldValue(field);
                                      return row.providerValue === value || !selectedProviderValues.includes(value);
                                    })
                                    .map((field) => {
                                      const value = fieldValue(field);
                                      const dataType = field.data_type ? ` (${field.data_type})` : '';
                                      const labelText = `${field.label || field.api_name || field.field_id}${dataType}`;
                                      return {
                                        value,
                                        label: labelText,
                                        searchText: `${labelText} ${field.api_name || ''} ${field.field_id || ''}`,
                                      };
                                    })}
                                />

                                <button
                                  type="button"
                                  onClick={() => removeMappingRow(connection, entity, row.id)}
                                  className="h-10 w-10 inline-flex items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                                  title="Remove mapping"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() => addMappingRow(connection, entity)}
                              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50"
                            >
                              <Plus size={16} /> Add Mapping
                            </button>
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>

                <div className="border-t border-slate-100 px-5 py-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => handleSaveMappings(platform)}
                    disabled={savingMappings === connection.id}
                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-70"
                  >
                    <Save size={16} /> {savingMappings === connection.id ? 'Saving...' : 'Save Mapping'}
                  </button>
                </div>
              </div>
            );
          })}

          {platforms.some((platform) => platform.connection?.status === 'connected') ? null : (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
              <h2 className="text-xl font-extrabold text-slate-900">No ERP connected yet</h2>
              <p className="mt-2 text-sm font-semibold text-slate-500">Connect an ERP platform first, then map its columns to Seatify fields.</p>
            </div>
          )}
        </div>
      </div>

      <Link to={backPath} className="sr-only">Back to institution</Link>
    </div>
  );
}
